import { useEffect, useMemo, useState } from 'react'
import { UnitLabel } from '@/shared/config/config'
import {
  InputGroup,
  Button,
  Card,
  CardHeader,
  CardBody,
  DataBlock,
  CardTitle,
  NumericInputGroup,
  isValidPercentage
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import calculatePFCEmissionsUsingOvervoltageMethod from '@/features/reports/formulas/calcualtePFCEmissionsUsingOvervoltageMethod'
import { Artifacts } from '@/api/EmissionReportModel'
import useI18n from '@/shared/hooks/useI18n'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { gwpc2f6, gwpcf4, overvoltageMethodConstants } from '../constants'

export default function OvervoltageMethodCalculatorPanel({
  isOpen,
  close,
  addEmission,
  initialValues,
  aluminiumQuantity,
  assetId
}: {
  isOpen: boolean
  close: () => void
  addEmission: (emission: number, artifacts: Artifacts) => void
  initialValues?: Artifacts
  aluminiumQuantity: number
  assetId: string
}) {
  const { t, numberFormatter3Decimals } = useI18n()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const constants = overvoltageMethodConstants[assetId as keyof typeof overvoltageMethodConstants]
  const [state, setState] = useState({
    cf4EmissionFactor: initialValues?.cf4EmissionFactor || constants?.cf4EmissionFactor || '',
    c2f6EmissionFactor: initialValues?.c2f6EmissionFactor || constants?.c2f6EmissionFactor || '',
    anodeEffectOvervoltagePerCell: initialValues?.anodeEffectOvervoltagePerCell || '',
    avgCurrentEfficiency: initialValues?.avgCurrentEfficiency || '',
    activityLevel: aluminiumQuantity,
    gwpcf4: initialValues?.gwpcf4 || gwpcf4 || '',
    gwpc2f6: initialValues?.gwpc2f6 || gwpc2f6 || '',
    collectionEfficiency: initialValues?.collectionEfficiency || '100'
  })

  const emission = calculatePFCEmissionsUsingOvervoltageMethod({
    cf4EmissionFactor: Number(state.cf4EmissionFactor),
    anodeEffectOvervoltagePerCell: Number(state.anodeEffectOvervoltagePerCell),
    avgCurrentEfficiency: Number(state.avgCurrentEfficiency) / 100,
    c2f6EmissionFactor: Number(state.c2f6EmissionFactor),
    activityLevel: Number(state.activityLevel),
    gwpcf4: Number(state.gwpcf4),
    gwpc2f6: Number(state.gwpc2f6),
    collectionEfficiency: Number(state.collectionEfficiency) / 100
  })

  const onAddClick = () => {
    if (!emission) {
      return
    }
    const artifacts = state
    addEmission(emission, artifacts)
    close()
  }

  useEffect(() => {
    if (isOpen && initialValues) {
      setState(prev => ({ ...initialValues, activityLevel: prev.activityLevel }))
    }
  }, [initialValues, isOpen])

  const isAddEnabled = useMemo(() => {
    return emission && emission > 0
  }, [emission])

  const formattedEmission = useMemo(() => {
    const emissionExistsAndNotInfinite = emission && emission !== Infinity
    const formatted = emissionExistsAndNotInfinite ? `${numberFormatter3Decimals(emission)}` : '0'
    return `${formatted} ${UnitLabel.tCO2e}`
  }, [emission, numberFormatter3Decimals])

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card>
        <CardHeader>
          <CardTitle title={t('report:calculators.overvoltageMethodCalculatorPanel.title')} />
          {t(`assets:${assetId}`)}
        </CardHeader>
        <CardBody className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="col-span-2">
              <NumericInputGroup
                name="anodeEffectOvervoltagePerCell"
                onChange={evt =>
                  setState({
                    ...state,
                    anodeEffectOvervoltagePerCell: evt.target.value
                  })
                }
                value={state.anodeEffectOvervoltagePerCell}
                label={t(
                  'report:calculators.overvoltageMethodCalculatorPanel.fields.anodeEffectOvervoltagePerCell.label'
                )}
                suffix={UnitLabel.mV}
                numericFormat={{
                  decimalScale: 3
                }}
              />
            </div>

            <div className="col-span-2">
              <NumericInputGroup
                name="avgCurrentEfficieny"
                onChange={evt =>
                  setState({
                    ...state,
                    avgCurrentEfficiency: evt.target.value
                  })
                }
                value={state.avgCurrentEfficiency}
                label={t(
                  'report:calculators.overvoltageMethodCalculatorPanel.fields.avgCurrentEfficiency.label'
                )}
                suffix={UnitLabel.percent}
                numericFormat={{
                  decimalScale: 0,
                  isAllowed: isValidPercentage
                }}
              />
            </div>
            <div className="col-span-2">
              <NumericInputGroup
                name="collectionEfficiency"
                suffix={UnitLabel.percent}
                onChange={evt =>
                  setState({
                    ...state,
                    collectionEfficiency: evt.target.value
                  })
                }
                numericFormat={{
                  decimalScale: 0,
                  isAllowed: isValidPercentage
                }}
                value={state.collectionEfficiency}
                label={t(
                  'report:calculators.overvoltageMethodCalculatorPanel.fields.collectionEfficiency.label'
                )}
              />
            </div>
            <NumericInputGroup
              name="cf4EmissionFactor"
              onChange={evt =>
                setState({
                  ...state,
                  cf4EmissionFactor: evt.target.value
                })
              }
              value={state.cf4EmissionFactor}
              suffix={UnitLabel.kgCF4PerTAlPerMv}
              label={t(
                'report:calculators.overvoltageMethodCalculatorPanel.fields.cf4EmissionFactor.label'
              )}
              numericFormat={{
                decimalScale: 3
              }}
            />

            <NumericInputGroup
              name="c2f6EmissionFactor"
              onChange={evt =>
                setState({
                  ...state,
                  c2f6EmissionFactor: evt.target.value
                })
              }
              value={state.c2f6EmissionFactor}
              label={t(
                'report:calculators.overvoltageMethodCalculatorPanel.fields.c2f6EmissionFactor.label'
              )}
              suffix={UnitLabel.tC2F6PerTAlPerCf4}
              numericFormat={{
                decimalScale: 3
              }}
            />

            <InputGroup
              name="gwpcf4"
              value={state.gwpcf4}
              disabled
              suffix={UnitLabel.tCO2ePerTCf4}
              label={t('report:calculators.overvoltageMethodCalculatorPanel.fields.gwpcf4.label')}
            />

            <InputGroup
              name="gwpc2f6"
              value={state.gwpc2f6}
              disabled
              suffix={UnitLabel.tCO2ePerTC2F6}
              label={t('report:calculators.overvoltageMethodCalculatorPanel.fields.gwpc2f6.label')}
            />
          </div>
          <DataBlock label={t('report:common:emission')} data={formattedEmission} />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 pb-4 px-4">
          <Button variant="subtle" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!isAddEnabled || !canEditReport} onClick={onAddClick}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
